import { useEffect, useId, useRef } from 'react';
import type { TFunction } from 'i18next';
import U2000CheckBoxArray from '../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxArray';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3149';
import type { U3149ActiveTask, U3149WorkNatureFormRow } from '../../../U3149Service';
import { u3149FilterActiveTasksCatalog, u3149FormatActiveTaskLabel } from '../../../U3149Service';
import { useU3149Context } from '../../../U3149Context';

interface U3149WorkNaturePunchableTasksBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3149WorkNatureFormRow>;
  t: TFunction;
  language: string;
}

function U3149SelectAllPunchableTasks({
  checked,
  indeterminate,
  disabled,
  label,
  onToggle,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  label: string;
  onToggle: () => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className="flex h-full items-center gap-3">
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onToggle()}
        className={`peer absolute h-5 w-5 opacity-0 ${disabled ? '' : 'cursor-pointer'}`}
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled}
      />
      <label
        htmlFor={inputId}
        className={`relative flex h-5 w-5 items-center justify-center rounded border transition-all duration-200
          ${
            disabled
              ? 'cursor-not-allowed surface-border bg-muted'
              : checked || indeterminate
                ? 'cursor-pointer border-blue-500 bg-blue-500 hover:border-blue-600 hover:bg-blue-600'
                : 'cursor-pointer border-gray-300 bg-white hover:border-gray-400 hover:bg-muted'
          }
          peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
        `}
      >
        {(checked || indeterminate) && (
          <svg
            className={disabled ? 'text-gray-400' : 'text-white'}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            width="14"
            height="14"
          >
            {indeterminate && !checked ? (
              <path d="M6 12h12" strokeLinecap="round" />
            ) : (
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        )}
      </label>
      <label
        htmlFor={inputId}
        className={`flex h-full select-none items-center text-sm font-medium leading-relaxed ${
          disabled ? 'cursor-not-allowed text-gray-500' : 'cursor-pointer text-gray-900 hover:text-gray-700'
        }`}
      >
        {label}
      </label>
    </div>
  );
}

function U3149WorkNaturePunchableTasksBody({ formStateHandlers, t, language }: U3149WorkNaturePunchableTasksBodyProps) {
  const { initLU } = useU3149Context();
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const activeTasks = u3149FilterActiveTasksCatalog(initLU.activeTasks);

  const selectedIds = new Set(data?.punchableTaskIds ?? []);
  const catalogIds = activeTasks.map((task) => task.id);
  const allSelected = catalogIds.length > 0 && catalogIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && selectedIds.size < catalogIds.length;

  if (!data) return null;

  const handleSelectAllChange = () => {
    if (readonly) return;
    const nextIds = allSelected || someSelected ? [] : [...catalogIds];
    formStateHandlers.setData({ ...data, punchableTaskIds: [...nextIds].sort() });
  };

  const tasksToRender: U3149ActiveTask[] = readonly
    ? activeTasks.filter((task) => selectedIds.has(task.id))
    : activeTasks;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <span className="text-sm font-medium text-gray-700">{t('lblPunchableTasksSection', { ns: NS })}</span>
        {!readonly && activeTasks.length > 0 && (
          <U3149SelectAllPunchableTasks
            checked={allSelected}
            indeterminate={someSelected}
            disabled={readonly}
            label={t('lblSelectAllPunchableTasks', { ns: NS })}
            onToggle={handleSelectAllChange}
          />
        )}
      </div>

      {activeTasks.length === 0 ? (
        <p className="text-sm text-gray-500">{t('msgNoActiveTasksForPunchables', { ns: NS })}</p>
      ) : readonly && selectedIds.size === 0 ? (
        <p className="text-sm text-gray-500">{t('msgNoPunchableTasksSelected', { ns: NS })}</p>
      ) : (
        <div
          className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          role="group"
          aria-label={t('lblPunchableTasksSection', { ns: NS })}
        >
          {tasksToRender.map((task) => (
            <U2000CheckBoxArray
              key={task.id}
              formStateHandlers={formStateHandlers}
              fieldName="punchableTaskIds"
              value={task.id}
              label={u3149FormatActiveTaskLabel(task, language)}
              readonly={readonly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default U3149WorkNaturePunchableTasksBody;
